import "./SystemStatus.scss"

import React, { useEffect, useState } from "react"
import { Modal, Button, Icon } from "@fider/components"
import { useFider } from "@fider/hooks"
import { actions } from "@fider/services"
import { AIStatus } from "@fider/services/actions/khcn"
import { getRealtimeStatus, onRealtimeStatusChange, subscribeToEvents, RealtimeStatus } from "@fider/services/realtime"
import IconCog from "@fider/assets/images/heroicons-cog.svg"
import IconSparkles from "@fider/assets/images/heroicons-sparkles-outline.svg"
import IconRss from "@fider/assets/images/heroicons-rss.svg"
import { i18n } from "@lingui/core"
import { Trans } from "@lingui/react/macro"

// Tùy biến KHCN: chip trạng thái cấu hình trên header, CHỈ quản trị viên / cộng tác viên thấy.
// Bấm mở modal xem: Gemini đã kết nối hay đang dùng bộ lọc nội bộ, và kết nối thời gian thực (SSE).

type Tone = "ok" | "warn" | "off"

const toneOf = (status: "ok" | "warn" | "off"): Tone => status

export const SystemStatus = () => {
  const fider = useFider()
  const canView = fider.session.isAuthenticated && (fider.session.user.isAdministrator || fider.session.user.isCollaborator)

  const [isOpen, setIsOpen] = useState(false)
  const [ai, setAi] = useState<AIStatus | undefined>(undefined)
  const [aiLoading, setAiLoading] = useState(false)
  const [realtime, setRealtime] = useState<RealtimeStatus>(getRealtimeStatus())

  // Theo dõi trạng thái kết nối thời gian thực; mở một kết nối rỗng để chip phản ánh đúng
  useEffect(() => {
    if (!canView) {
      return
    }
    const stopSub = subscribeToEvents(-1, () => undefined) // -1: không khớp bài nào, chỉ để giữ kết nối
    setRealtime(getRealtimeStatus())
    const stopStatus = onRealtimeStatusChange(setRealtime)
    return () => {
      stopSub()
      stopStatus()
    }
  }, [canView])

  // Tải trạng thái AI ngay khi gắn để chip hiện đúng màu; làm mới mỗi khi mở modal
  useEffect(() => {
    if (!canView) {
      return
    }
    let cancelled = false
    setAiLoading(true)
    actions.getAIStatus().then((r) => {
      if (cancelled) {
        return
      }
      setAiLoading(false)
      if (r.ok) {
        setAi(r.data)
      }
    })
    return () => {
      cancelled = true
    }
  }, [canView, isOpen])

  if (!canView) {
    return null
  }

  const aiTone: Tone = !ai ? "off" : !ai.enabled ? "off" : ai.available ? "ok" : "warn"
  const realtimeTone: Tone = realtime === "open" ? "ok" : realtime === "connecting" ? "warn" : "off"
  // Chip tổng: đỏ nếu có "off", vàng nếu có "warn", xanh nếu tất cả ok
  const overall: Tone = [aiTone, realtimeTone].includes("off") ? "off" : [aiTone, realtimeTone].includes("warn") ? "warn" : "ok"

  const aiLabel = () => {
    if (aiLoading || !ai) {
      return i18n._({ id: "khcn.status.loading", message: "Checking..." })
    }
    if (!ai.enabled) {
      return i18n._({ id: "khcn.status.ai.off", message: "API key not configured — using built-in filter" })
    }
    if (ai.available) {
      return i18n._({ id: "khcn.status.ai.ok", message: "Connected to Gemini ({model})", values: { model: ai.model } })
    }
    return i18n._({
      id: "khcn.status.ai.warn",
      message: "Gemini paused, using built-in filter{reason}",
      values: { reason: ai.reason ? `: ${ai.reason}` : "" },
    })
  }

  const realtimeLabel = () => {
    switch (realtime) {
      case "open":
        return i18n._({ id: "khcn.status.rt.ok", message: "Connected" })
      case "connecting":
        return i18n._({ id: "khcn.status.rt.connecting", message: "Connecting..." })
      default:
        return i18n._({ id: "khcn.status.rt.off", message: "Not connected" })
    }
  }

  const toneWord = (tone: Tone) =>
    tone === "ok"
      ? i18n._({ id: "khcn.status.badge.ok", message: "Connected" })
      : tone === "warn"
      ? i18n._({ id: "khcn.status.badge.warn", message: "Fallback" })
      : i18n._({ id: "khcn.status.badge.off", message: "Off" })

  const row = (tone: Tone, icon: SpriteSymbol, title: React.ReactNode, detail: string) => (
    <div className={`c-sysstatus__row c-sysstatus__row--${toneOf(tone)}`}>
      <span className="c-sysstatus__icon" aria-hidden="true">
        <Icon sprite={icon} />
      </span>
      <div className="c-sysstatus__rowtext">
        <span className="c-sysstatus__rowtitle">{title}</span>
        <span className="c-sysstatus__rowdetail">{detail}</span>
      </div>
      <span className={`c-sysstatus__badge c-sysstatus__badge--${toneOf(tone)}`}>
        <span className="c-sysstatus__badge-dot" aria-hidden="true" />
        {toneWord(tone)}
      </span>
    </div>
  )

  return (
    <>
      <button
        type="button"
        className={`c-sysstatus-chip c-sysstatus-chip--${overall}`}
        onClick={() => setIsOpen(true)}
        aria-label={i18n._({ id: "khcn.status.title", message: "System status" })}
        title={i18n._({ id: "khcn.status.title", message: "System status" })}
      >
        <span className="c-sysstatus-chip__led" aria-hidden="true" />
        <Icon sprite={IconCog} />
      </button>

      <Modal.Window isOpen={isOpen} onClose={() => setIsOpen(false)} size="small" className="c-sysstatus-modal">
        <Modal.Header>
          <span className="c-sysstatus__heading">
            <Trans id="khcn.status.title">System status</Trans>
          </span>
        </Modal.Header>
        <Modal.Content>
          <div className="c-sysstatus">
            {row(aiTone, IconSparkles, i18n._({ id: "khcn.status.ai.name", message: "AI content moderation" }), aiLabel())}
            {row(realtimeTone, IconRss, i18n._({ id: "khcn.status.rt.name", message: "Real-time updates" }), realtimeLabel())}
          </div>
        </Modal.Content>
        <Modal.Footer>
          <Button variant="tertiary" onClick={() => setIsOpen(false)}>
            <Trans id="action.close">Close</Trans>
          </Button>
        </Modal.Footer>
      </Modal.Window>
    </>
  )
}
